/* HY Main product: gallery carousel, "buy now" form submit, ATC label upkeep.
   Variant selection reuses Dawn's <variant-selects> (global.js) with an instant local price/button update that the
   server's section render then confirms;
   the drawer mode reuses <product-form>. */
(() => {
  if (!customElements.get('hy-mp-gallery')) {
    customElements.define('hy-mp-gallery', class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.hy-mp__track');
        this.slides = [...this.querySelectorAll('.hy-mp__slide')];
        this.thumbWrap = this.querySelector('.hy-mp__thumbs');
        this.thumbs = [...this.querySelectorAll('.hy-mp__thumb')];
        this.index = -1;
        this.querySelector('.hy-mp__arrow--prev')?.addEventListener('click', () => this.go(this.index - 1));
        this.querySelector('.hy-mp__arrow--next')?.addEventListener('click', () => this.go(this.index + 1));
        this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.index))));
        this.addEventListener('click', (e) => {
          const play = e.target.closest('.hy-mp__play');
          if (!play) return;
          const box = play.closest('[data-video]');
          box.replaceChildren(box.querySelector('template').content.cloneNode(true));
        });
        // Slides other than the first visible one ship with data-src/data-srcset so they don't compete
        // with above-the-fold images. Swap them in on first interaction, or once the page is idle.
        const wake = () => this.hydrate();
        ['pointerdown', 'touchstart', 'mouseenter', 'focusin'].forEach((t) => this.addEventListener(t, wake, { once: true, passive: true }));
        // Safari has no requestIdleCallback: fall back to a real 3s delay (an options object as setTimeout's delay means 0).
        const idle = () => ('requestIdleCallback' in window ? window.requestIdleCallback(wake, { timeout: 3000 }) : setTimeout(wake, 3000));
        if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });
        let raf;
        this.track.addEventListener('scroll', () => {
          if (!this.initialJump) this.hydrate();
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => this.sync());
        }, { passive: true });
        const start = Number(this.dataset.start || 0);
        const thumbStart = Number(this.dataset.thumbStart || 0);
        const mobile = window.matchMedia('(max-width: 767px)').matches;
        // Initial jumps load only the slide shown; the rest still wait for interaction / idle.
        // The jump's own scroll event must not count as the shopper scrolling.
        this.initialJump = true;
        setTimeout(() => { this.initialJump = false; }, 600);
        if (start > 0) this.go(start, 'instant', true);
        else if (mobile && thumbStart > 0) this.go(thumbStart, 'instant', true);
        else this.sync();
        // Like GemPages: the thumbnail strip opens at the first available variant's image and stays there
        // until the shopper uses the gallery.
        if (thumbStart > 0 && this.thumbWrap && this.thumbs[thumbStart]) {
          this.holdThumbs = true;
          // Thumb widths settle after first layout (and again after load), so re-apply while still held.
          const place = () => { if (this.holdThumbs) this.thumbWrap.scrollTo({ left: this.thumbs[thumbStart].offsetLeft, behavior: 'instant' }); };
          requestAnimationFrame(() => requestAnimationFrame(place));
          if (document.readyState === 'complete') setTimeout(place, 0); else window.addEventListener('load', place, { once: true });
          const release = () => { this.holdThumbs = false; };
          ['pointerdown', 'keydown'].forEach((t) => this.addEventListener(t, release, { once: true, passive: true }));
        }
      }

      hydrate(scope) {
        if (this.hydrated) return;
        if (!scope) this.hydrated = true;
        (scope || this).querySelectorAll('img[data-srcset]').forEach((img) => {
          img.sizes = img.dataset.sizes || img.sizes;
          img.srcset = img.dataset.srcset;
          img.src = img.dataset.src;
          img.removeAttribute('data-srcset');
        });
      }

      go(i, behavior = 'smooth', initial = false) {
        if (!this.slides.length) return;
        i = Math.max(0, Math.min(this.slides.length - 1, i));
        this.hydrate(initial ? this.slides[i] : undefined);
        this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior });
        this.mark(i);
      }

      sync() {
        const w = this.track.clientWidth || 1;
        this.mark(Math.round(this.track.scrollLeft / w));
      }

      mark(i) {
        if (i === this.index) return;
        this.index = i;
        this.toggleAttribute('data-at-start', i <= 0);
        this.toggleAttribute('data-at-end', i >= this.slides.length - 1);
        this.thumbs.forEach((t, n) => {
          t.classList.toggle('is-active', n === i);
          if (n === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
        });
        const thumb = this.thumbs[i];
        if (thumb && this.thumbWrap && !this.holdThumbs) this.thumbWrap.scrollTo({ left: thumb.offsetLeft, behavior: "smooth" });
      }

      // Called by Dawn's VariantSelects with "<sectionId>-<mediaId>".
      setActiveMedia(mediaId) {
        const i = this.slides.findIndex((s) => s.dataset.mediaId === mediaId);
        this.holdThumbs = false;
        if (i > -1) this.go(i);
      }
    });
  }

  if (!customElements.get('hy-buy-now')) {
    customElements.define('hy-buy-now', class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.button = this.querySelector('[type="submit"]');
        this.form.addEventListener('submit', (e) => this.submit(e));
      }

      submit(e) {
        e.preventDefault();
        if (!this.button || this.button.getAttribute('aria-disabled') === 'true') return;
        this.error();
        this.button.setAttribute('aria-disabled', 'true');
        this.button.classList.add('loading');
        this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
        fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(this.form),
        })
          .then(async (r) => {
            let res = null;
            try { res = await r.json(); } catch (e) { /* non-JSON response */ }
            // Shopify reports problems (e.g. stock) as JSON with a status; any other failure gets the generic text.
            if (!r.ok || !res || res.status) {
              const err = new Error((res && (res.description || res.message)) || '');
              err.fromShopify = true;
              throw err;
            }
            window.location.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'checkout';
          })
          .catch((err) => {
            // Not retried automatically: the item may have been added even if the response was lost.
            // Only Shopify's own message is shown verbatim; network errors ("Failed to fetch") get the friendly text.
            this.error((err && err.fromShopify && err.message) || this.dataset.errorText || 'Cannot add product to cart');
            this.reset();
          });
      }

      reset() {
        this.button.classList.remove('loading');
        this.button.removeAttribute('aria-disabled');
        this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
      }

      error(msg) {
        const wrap = this.querySelector('.product-form__error-message-wrapper');
        if (!wrap) return;
        wrap.toggleAttribute('hidden', !msg);
        if (msg) wrap.querySelector('.product-form__error-message').textContent = msg;
      }
    });
  }

  // Variant change with an instant local update: for <variant-selects data-hy-local> (hy-main-product) the price row
  // comes from the per-variant rows rendered next to it (script[data-hy-price-rows], same Liquid snippet) and the add
  // button follows the variant's page-load availability, applied synchronously. The section is still fetched in the
  // background as Dawn did (?variant=&section_id=); when the server's price row or button state differs from what was
  // shown (price or stock changed since the page loaded) the server's version replaces it and variantChange is
  // published again. Dawn's own product template keeps the original fetch only.
  const localVariants = () => {
    const VariantSelects = customElements.get('variant-selects');
    if (!VariantSelects || VariantSelects.prototype.hyLocalRender) return;
    const fetchRender = VariantSelects.prototype.renderProductInfo;
    const normalize = (html) => { const t = document.createElement('template'); t.innerHTML = html; return t.innerHTML; };
    VariantSelects.prototype.hyLocalRender = true;
    VariantSelects.prototype.renderProductInfo = function () {
      if (!this.hasAttribute('data-hy-local')) return fetchRender.call(this);
      const variant = this.currentVariant;
      const sectionId = this.dataset.section;
      const price = document.getElementById(`price-${sectionId}`);
      let row = null;
      if (price) {
        if (this.hyPriceRows === undefined) {
          const source = price.parentElement.querySelector('script[data-hy-price-rows]');
          try { this.hyPriceRows = source ? JSON.parse(source.textContent) : null; } catch (e) { this.hyPriceRows = null; }
        }
        row = this.hyPriceRows && this.hyPriceRows[variant.id];
        if (typeof row !== 'string') return fetchRender.call(this);
        price.innerHTML = row;
        price.classList.remove('visibility-hidden');
      }
      const disabled = !variant.available;
      this.toggleAddButton(disabled, window.variantStrings.soldOut);
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId, html: document, variant } });

      fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${sectionId}`)
        .then((response) => response.text())
        .then((responseText) => {
          if (!this.currentVariant || this.currentVariant.id !== variant.id) return;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const source = html.getElementById(`price-${sectionId}`);
          const button = html.getElementById(`ProductSubmitButton-${sectionId}`);
          const serverDisabled = button ? button.hasAttribute('disabled') : true;
          const priceChanged = !!(price && source && normalize(source.innerHTML) !== normalize(row));
          if (!priceChanged && serverDisabled === disabled) return;
          if (priceChanged) price.innerHTML = source.innerHTML;
          this.toggleAddButton(serverDisabled, window.variantStrings.soldOut);
          if (typeof publish === 'function') publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId, html, variant } });
        })
        .catch(() => {});
    };
  };
  if (customElements.get('variant-selects')) localVariants();
  else customElements.whenDefined('variant-selects').then(localVariants);

  // Dawn's VariantSelects writes its default "Add to cart" text into the button; restore our label.
  const relabel = () => {
    document.querySelectorAll('.hy-mp__atc').forEach((btn) => {
      const label = btn.querySelector('.hy-mp__atc-label');
      if (!label) return;
      const want = btn.hasAttribute('disabled') ? btn.dataset.soldOut : btn.dataset.label;
      if (want && label.textContent !== want && (btn.hasAttribute('disabled') ? label.textContent !== (window.variantStrings || {}).unavailable : true)) label.textContent = want;
    });
  };
  const hook = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(relabel));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hook); else hook();
})();
